/**
 * ProcessThread Loading Service
 * Orchestrates loading complete process thread data from backend APIs
 */

import {
  projectApiClient,
  processApiClient,
  processThreadApiClient,
  processElementApiClient,
  connectionApiClient,
} from '@flow-studio/api';
import {
  Project,
  Process,
  ProcessThread,
  ProcessElement,
  Connection,
  Connector,
} from '@flow-studio/api';
import { WorkflowNode, WorkflowConnection } from '@flow-studio/types';
import {
  mapProcessElementsToNodes,
  mapConnectionsToEdges,
} from '../mappers/backendToUiMapper';

/**
 * Complete ProcessThread Data
 * Contains all business data and UI-ready data for rendering a process thread
 */
export interface CompleteProcessThreadData {
  // Business domain data
  project: Project;
  process: Process;
  processThread: ProcessThread;
  processElements: ProcessElement[];
  connections: Connection[];
  connectors: Map<number, Connector>; // ConnectorID → Connector

  // UI-ready data (mapped from business domain)
  nodes: WorkflowNode[];
  edges: WorkflowConnection[];
}

/**
 * ProcessThread Loading Service
 */
export class ProcessThreadLoadingService {
  /**
   * Load complete process thread by project ID
   * Loads the first process and first thread by default
   */
  async loadByProject(projectId: number): Promise<CompleteProcessThreadData | null> {
    try {
      // 1. Load project
      const projectResponse = await projectApiClient.getById({ id: projectId });
      if (!projectResponse.success || !projectResponse.data) {
        console.error('Failed to load project:', projectResponse.error);
        return null;
      }
      const project = projectResponse.data;

      // 2. Load processes for this project
      const processesResponse = await processApiClient.getByProject(projectId);
      if (!processesResponse.success || !processesResponse.data || processesResponse.data.length === 0) {
        console.error('No processes found for project');
        return null;
      }
      const process = processesResponse.data[0]; // Use first process

      // 3. Load process thread data
      return await this.loadByProcess(process.ProcessID, project);
    } catch (error) {
      console.error('Error loading workflow by project:', error);
      return null;
    }
  }

  /**
   * Load complete process thread by process ID
   */
  async loadByProcess(processId: number, project?: Project): Promise<CompleteProcessThreadData | null> {
    try {
      // 1. Load process if not provided
      let process: Process | undefined;
      if (!project) {
        const processResponse = await processApiClient.getById({ id: processId });
        if (!processResponse.success || !processResponse.data) {
          console.error('Failed to load process');
          return null;
        }
        process = processResponse.data;

        // Load project
        if (process.ProjectID) {
          const projectResponse = await projectApiClient.getById({ id: process.ProjectID });
          project = projectResponse.data;
        }
      }

      // 2. Load process threads for this process
      const threadsResponse = await processThreadApiClient.getByProcess(processId);
      if (!threadsResponse.success || !threadsResponse.data || threadsResponse.data.length === 0) {
        console.error('No process threads found');
        return null;
      }
      const processThread = threadsResponse.data[0]; // Use first thread

      // 3. Load process thread data
      return await this.loadByThread(processThread.ProcessThreadID, project!, process);
    } catch (error) {
      console.error('Error loading process thread by process:', error);
      return null;
    }
  }

  /**
   * Load complete process thread by thread ID
   * @param threadId - ProcessThread ID
   * @param project - Optional project (skips fetch if provided)
   * @param process - Optional process (skips fetch if provided)
   * @param processThreadOverride - Optional pre-loaded thread (skips getById re-fetch if provided)
   */
  async loadByThread(
    threadId: number,
    project?: Project,
    process?: Process,
    processThreadOverride?: ProcessThread
  ): Promise<CompleteProcessThreadData | null> {
    try {
      // 1. Use provided processThread or fetch it
      let processThread: ProcessThread;
      if (processThreadOverride) {
        processThread = processThreadOverride;
      } else {
        const threadResponse = await processThreadApiClient.getById({ id: threadId });
        if (!threadResponse.success || !threadResponse.data) {
          console.error('Failed to load process thread:', threadResponse.error);
          return null;
        }
        processThread = threadResponse.data;
      }

      // 2. Load process if not provided
      if (!process) {
        const processResponse = await processApiClient.getById({ id: processThread.ProcessID });
        if (processResponse.success && processResponse.data) {
          process = processResponse.data;
        }
      }

      // 3. Load project if not provided
      if (!project && process?.ProjectID) {
        const projectResponse = await projectApiClient.getById({ id: process.ProjectID });
        if (projectResponse.success && projectResponse.data) {
          project = projectResponse.data;
        }
      }

      // 4. Get current version ID
      const versionId = processThread.CurrentVersionID;

      let processElements: ProcessElement[] = [];
      let connections: Connection[] = [];

      if (versionId) {
        // 5. Load process elements for this version (non-fatal: open empty canvas on failure)
        const elementsResponse = await processElementApiClient.getByThreadVersion(versionId);
        if (elementsResponse.success) {
          processElements = elementsResponse.data ?? [];
        } else {
          console.warn('Failed to load process elements, continuing with empty:', elementsResponse.error);
        }

        // 6. Load connections for this version (non-fatal: log warning and continue with empty)
        const connectionsResponse = await connectionApiClient.getByThreadVersion(versionId);
        if (connectionsResponse.success) {
          connections = connectionsResponse.data ?? [];
        } else {
          console.warn('Failed to load connections, continuing with empty:', connectionsResponse.error);
        }
      }

      // NOTE: Connector data loading removed
      // The connectorID values are already available in processElements
      // When connector details are needed, the dialog will fetch them on-demand
      // This eliminates unnecessary API calls at workflow load time
      const connectorsMap = new Map<number, Connector>();

      // 8. Convert to UI models
      const nodes = mapProcessElementsToNodes(processElements);
      const edges = this.mapConnectionsWithCorrectNodeIds(connections, processElements);

      // Real, reported bug: `project`/`process` above are only fetched "if not provided" and
      // their own fetch failures (a 404, a deleted record, a network error) were never checked
      // here — this used to return `project: project!`/`process: process!`, a bare non-null
      // assertion with no runtime guard, so a failed fetch silently produced a
      // `CompleteProcessThreadData` with `project`/`process` actually `undefined` at runtime
      // despite the type claiming otherwise. `WorkflowApp.tsx`'s breadcrumb builder trusted that
      // promise and crashed the whole app (`Cannot read properties of undefined (reading 'Name')`)
      // the moment it rendered. `project`/`process` are load-bearing, non-optional fields of
      // `CompleteProcessThreadData` — if either couldn't be resolved, this is a genuine load
      // failure, same as an empty `processThread`/`processElements` result elsewhere in this
      // file, not a "best-effort, render anyway" situation.
      if (!project || !process) {
        console.error('Failed to resolve project/process for this thread — cannot open in Designer.');
        return null;
      }

      return {
        project,
        process,
        processThread,
        processElements,
        connections,
        connectors: connectorsMap,
        nodes,
        edges,
      };
    } catch (error) {
      console.error('Error loading workflow by thread:', error);
      return null;
    }
  }

  /**
   * Map connections with correct node IDs
   * Connections reference ProcessElementID, but React Flow nodes use ProcessElementKey
   */
  private mapConnectionsWithCorrectNodeIds(
    connections: Connection[],
    elements: ProcessElement[]
  ): WorkflowConnection[] {
    // Create map: ProcessElementID → ProcessElementKey
    const idToKeyMap = new Map<number, string>();
    elements.forEach(el => {
      idToKeyMap.set(el.ProcessElementID, el.ProcessElementKey);
    });

    // Map connections to edges with correct node IDs
    const edges = mapConnectionsToEdges(connections);

    return edges.map(edge => {
      // Extract ProcessElementID from source/target
      const sourceId = parseInt(edge.source.replace('element-', ''));
      const targetId = parseInt(edge.target.replace('element-', ''));

      // Replace with ProcessElementKey
      const sourceKey = idToKeyMap.get(sourceId);
      const targetKey = idToKeyMap.get(targetId);

      if (!sourceKey || !targetKey) {
        console.warn('Could not find ProcessElementKey for connection:', edge);
      }

      return {
        ...edge,
        source: sourceKey || edge.source,
        target: targetKey || edge.target,
      };
    });
  }
}

// Singleton instance
export const processThreadLoadingService = new ProcessThreadLoadingService();

// Legacy export for backward compatibility (deprecated)
export const workflowLoadingService = processThreadLoadingService;
